import React, { useEffect, useState } from "react";
import { Droplets, Timer, X } from "lucide-react";
import { Button } from "./ui/button";

const DURATION_PRESETS = [
  { label: "1 min", sec: 60 },
  { label: "2 min", sec: 120 },
  { label: "5 min", sec: 300 },
  { label: "10 min", sec: 600 },
  { label: "15 min", sec: 900 },
  { label: "30 min", sec: 1800 },
];

export default function PumpCommandDialog({
  open,
  action,
  deviceLabel,
  busy,
  nodes = [],
  selectedNodeId = "",
  onSelectNode,
  onConfirm,
  onCancel,
}) {
  const [durationSec, setDurationSec] = useState(120);

  useEffect(() => {
    if (open && action === "on") setDurationSec(120);
  }, [open, action]);

  if (!open) return null;

  const isOn = action === "on";
  const isHub = Array.isArray(nodes) && nodes.length > 0;
  const selected = nodes.find((n) => n.nodeId === selectedNodeId) || nodes[0];
  const title = isOn ? (isHub ? "Water a pot" : "Turn pump ON") : "Turn pump OFF";
  const subtitle = isOn
    ? isHub
      ? "Pick which pot valve to open. The hub pump starts only after that node confirms."
      : "Choose how long the pump should run. It will stop automatically when the timer ends."
    : "Stop the pump immediately. This also tells the open valve to close.";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pump-dialog-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
        aria-label="Close dialog"
        onClick={busy ? undefined : onCancel}
      />
      <div className="relative w-full max-w-md rounded-3xl border border-border bg-card shadow-elevated animate-[slide-down_0.25s_var(--ease-smooth)]">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-accent/15 text-accent">
              <Droplets className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 id="pump-dialog-title" className="font-display text-xl text-foreground">
                {title}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{subtitle}</p>
              {deviceLabel ? (
                <p className="mt-2 text-xs font-medium uppercase tracking-widest text-accent">
                  {deviceLabel}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:bg-accent/10 text-muted-foreground transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isOn ? (
          <div className="px-5 py-4">
            {isHub ? (
              <div className="mb-4">
                <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  Which pot needs water
                </div>
                <div className="mt-2 grid gap-2">
                  {nodes.map((n) => {
                    const active = selected?.nodeId === n.nodeId;
                    const offline = n.online === false;
                    return (
                      <button
                        key={n.nodeId}
                        type="button"
                        disabled={busy || offline}
                        onClick={() => onSelectNode?.(n.nodeId)}
                        className={`rounded-2xl border px-3 py-2.5 text-left text-sm transition-all ${
                          offline
                            ? "cursor-not-allowed border-border/60 bg-muted/30 text-muted-foreground"
                            : active
                              ? "border-accent bg-accent/10 text-foreground"
                              : "border-border bg-cream/40 text-foreground hover:border-accent/40"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{n.name || n.nodeId}</span>
                          <span className="text-xs text-muted-foreground">
                            {offline
                              ? "Offline"
                              : n.potIndex
                                ? `Pot ${n.potIndex}`
                                : n.nodeId}
                            {!offline && n.soil != null ? ` · soil ${Math.round(n.soil)}%` : ""}
                          </span>
                        </div>
                        {!offline && n.needsWater ? (
                          <div className="mt-1 text-xs text-accent">Needs water</div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
              <Timer className="h-3.5 w-3.5" />
              Auto-off timer
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {DURATION_PRESETS.map((preset) => (
                <button
                  key={preset.sec}
                  type="button"
                  disabled={busy}
                  onClick={() => setDurationSec(preset.sec)}
                  className={`rounded-2xl border px-3 py-2.5 text-sm font-medium transition-all ${
                    durationSec === preset.sec
                      ? "border-accent bg-accent text-accent-foreground shadow-soft"
                      : "border-border bg-cream/40 text-foreground hover:border-accent/40 hover:bg-accent/10"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              You can still tap Pump OFF anytime to stop early.
            </p>
          </div>
        ) : (
          <div className="px-5 py-4">
            <div className="rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
              The pump will stop as soon as the device receives this command (usually within a few seconds).
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          <Button variant="outline" type="button" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() =>
              onConfirm(
                isOn
                  ? {
                      durationSec,
                      nodeId: selected?.nodeId,
                      potIndex: selected?.potIndex,
                    }
                  : {}
              )
            }
            className={isOn ? "" : "border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15"}
          >
            {busy ? "Sending…" : isOn ? "Start pump" : "Stop pump"}
          </Button>
        </div>
      </div>
    </div>
  );
}
